import * as THREE from 'three';
import { getCanvasResolution } from '../FikoCanvasResolution';
import {
  MeshBVH,
  MeshBVHOptions,
  SAH,
  computeBoundsTree,
  disposeBoundsTree
} from 'three-mesh-bvh';

/**
 * ULTRA PERFORMANS: Large Geometry Handler
 * Çok büyük tek mesh'lerin ilk seçiminde donma sorununu çözer
 *
 * Problem:
 * - Milyon+ triangle mesh'lerde ilk raycast 500ms-2000ms sürebilir
 * - BVH oluşturma blocking ve yavaş
 * - UI donuyor
 *
 * Çözüm:
 * - Progressive BVH: Arka planda chunk'lar halinde BVH oluştur
 * - Approximate Selection: BVH hazır olana kadar bounding box tabanlı seçim
 * - Pre-warming: Mesh import edildiğinde BVH'ı önceden hazırla
 * - Web Worker: BVH oluşturmayı worker'a taşı
 *
 * Features:
 * - Non-blocking BVH construction
 * - Progressive mesh selection (coarse → fine)
 * - Automatic large mesh detection
 * - Memory-efficient chunked processing
 * - Worker-based BVH building
 */

// BVH prototype patching is handled centrally by FikoRaycastBVH.patchPrototypes()
// Do NOT patch prototypes here to avoid duplicate/racing patches.

interface LargeMeshInfo {
  mesh: THREE.Mesh;
  triangleCount: number;
  bvhStatus: 'none' | 'building' | 'ready' | 'failed';
  bvhProgress: number;          // 0-100
  boundingBox: THREE.Box3;
  boundingSphere: THREE.Sphere;
  lastAccessTime: number;
  priority: number;             // Daha yakın/görünür = daha yüksek öncelik
  buildStartTime?: number;
}

interface SelectionResult {
  object: THREE.Object3D | null;
  point: THREE.Vector3 | null;
  distance: number;
  face: THREE.Face | null;
  faceIndex: number | null;
  isApproximate: boolean;       // BVH hazır değilse true
  bvhProgress: number;          // BVH build progress
}

// Pre-allocated vectors for zero-allocation operations
const _tempBox3 = new THREE.Box3();
const _tempSphere = new THREE.Sphere();
const _tempVec3 = new THREE.Vector3();
const _tempMatrix = new THREE.Matrix4();
const _rayOrigin = new THREE.Vector3();
const _rayDirection = new THREE.Vector3();

export class FikoLargeGeometryHandler {
  // Mesh registry
  private largeMeshes = new Map<string, LargeMeshInfo>();

  // Thresholds
  private config = {
    largeTriangleThreshold: 50000,      // Bu üstü "büyük" mesh
    hugeTriangleThreshold: 500000,      // Bu üstü "devasa" mesh
    maxBVHBuildTimePerFrame: 8,         // ms (16ms frame budget'ın yarısı)
    prewarmOnImport: true,
    useWorker: true,
    approximateSelectionEnabled: true,
    maxConcurrentBuilds: 2
  };

  // Build queue
  private buildQueue: LargeMeshInfo[] = [];
  private currentlyBuilding = 0;

  // Worker for BVH construction
  private bvhWorker: Worker | null = null;
  private workerPending = new Map<string, (success: boolean) => void>();

  // 🚀 PERFORMANS OPT: Track blob URL for cleanup
  private bvhWorkerBlobUrl: string | null = null;

  // Stats
  private stats = {
    largeMeshCount: 0,
    bvhReady: 0,
    bvhBuilding: 0,
    approximateSelections: 0,
    preciseSelections: 0,
    totalBuildTime: 0,
    avgBuildTime: 0
  };

  // Frame tracking
  private frameCount = 0;
  private lastUpdateTime = 0;

  constructor() {
    this.initWorker();
  }

  /**
   * Initialize Web Worker for BVH construction
   * FAZA 3.2: Enhanced worker with real BVH construction
   */
  private initWorker(): void {
    if (!this.config.useWorker || typeof Worker === 'undefined') {
      return;
    }

    // 🚀 FAZA 3.2: Enhanced BVH Worker with SAH-based construction
    // This worker performs actual BVH building off the main thread
    const workerCode = `
      // BVH Builder Worker - Inline version
      class WorkerBVHBuilder {
        constructor(meshId, positions, indices, options) {
          this.meshId = meshId;
          this.positions = positions;
          this.indices = indices;
          this.maxLeafTris = options.maxLeafTris || 10;
          this.triangleCount = indices ? indices.length / 3 : positions.length / 9;
          this.startTime = performance.now();
        }

        build() {
          try {
            self.postMessage({ type: 'progress', meshId: this.meshId, progress: 0, stage: 'init' });

            // Compute triangle data
            this.computeTriangleData();
            self.postMessage({ type: 'progress', meshId: this.meshId, progress: 30, stage: 'sorting' });

            // Build tree
            const root = this.buildNode(0, this.triangleCount, 0);
            self.postMessage({ type: 'progress', meshId: this.meshId, progress: 90, stage: 'building' });

            // Complete
            self.postMessage({ type: 'progress', meshId: this.meshId, progress: 100, stage: 'complete' });

            return {
              roots: root ? [root] : [],
              bounds: this.computeOverallBounds()
            };
          } catch (e) {
            return null;
          }
        }

        computeTriangleData() {
          this.triangleCentroids = new Float32Array(this.triangleCount * 3);
          this.triangleBounds = new Float32Array(this.triangleCount * 6);
          this.triangleIndices = new Uint32Array(this.triangleCount);

          for (let i = 0; i < this.triangleCount; i++) {
            this.triangleIndices[i] = i;
            const v0 = this.getVertex(i, 0);
            const v1 = this.getVertex(i, 1);
            const v2 = this.getVertex(i, 2);

            // Centroid
            this.triangleCentroids[i * 3] = (v0[0] + v1[0] + v2[0]) / 3;
            this.triangleCentroids[i * 3 + 1] = (v0[1] + v1[1] + v2[1]) / 3;
            this.triangleCentroids[i * 3 + 2] = (v0[2] + v1[2] + v2[2]) / 3;

            // Bounds
            this.triangleBounds[i * 6] = Math.min(v0[0], v1[0], v2[0]);
            this.triangleBounds[i * 6 + 1] = Math.min(v0[1], v1[1], v2[1]);
            this.triangleBounds[i * 6 + 2] = Math.min(v0[2], v1[2], v2[2]);
            this.triangleBounds[i * 6 + 3] = Math.max(v0[0], v1[0], v2[0]);
            this.triangleBounds[i * 6 + 4] = Math.max(v0[1], v1[1], v2[1]);
            this.triangleBounds[i * 6 + 5] = Math.max(v0[2], v1[2], v2[2]);
          }
        }

        getVertex(tri, v) {
          let idx = this.indices ? this.indices[tri * 3 + v] * 3 : (tri * 3 + v) * 3;
          return [this.positions[idx], this.positions[idx + 1], this.positions[idx + 2]];
        }

        buildNode(start, end, depth) {
          const count = end - start;
          if (count === 0) return null;

          const bounds = this.computeBoundsForRange(start, end);

          if (count <= this.maxLeafTris || depth > 40) {
            return { boundingData: bounds, offset: start, count, left: null, right: null, isLeaf: true };
          }

          const { axis, splitPos } = this.findBestSplit(start, end, bounds);
          const mid = this.partition(start, end, axis, splitPos);

          if (mid === start || mid === end) {
            const midIndex = Math.floor((start + end) / 2);
            return {
              boundingData: bounds, offset: 0, count: 0,
              left: this.buildNode(start, midIndex, depth + 1),
              right: this.buildNode(midIndex, end, depth + 1),
              isLeaf: false
            };
          }

          return {
            boundingData: bounds, offset: 0, count: 0,
            left: this.buildNode(start, mid, depth + 1),
            right: this.buildNode(mid, end, depth + 1),
            isLeaf: false
          };
        }

        computeBoundsForRange(start, end) {
          let minX = Infinity, minY = Infinity, minZ = Infinity;
          let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
          for (let i = start; i < end; i++) {
            const ti = this.triangleIndices[i] * 6;
            minX = Math.min(minX, this.triangleBounds[ti]);
            minY = Math.min(minY, this.triangleBounds[ti + 1]);
            minZ = Math.min(minZ, this.triangleBounds[ti + 2]);
            maxX = Math.max(maxX, this.triangleBounds[ti + 3]);
            maxY = Math.max(maxY, this.triangleBounds[ti + 4]);
            maxZ = Math.max(maxZ, this.triangleBounds[ti + 5]);
          }
          return new Float32Array([minX, minY, minZ, maxX, maxY, maxZ]);
        }

        findBestSplit(start, end, bounds) {
          const ex = bounds[3] - bounds[0];
          const ey = bounds[4] - bounds[1];
          const ez = bounds[5] - bounds[2];
          let axis = 0;
          if (ey > ex && ey > ez) axis = 1;
          else if (ez > ex && ez > ey) axis = 2;
          return { axis, splitPos: (bounds[axis] + bounds[axis + 3]) / 2 };
        }

        partition(start, end, axis, splitPos) {
          let left = start, right = end - 1;
          while (left <= right) {
            const li = this.triangleIndices[left] * 3;
            if (this.triangleCentroids[li + axis] < splitPos) {
              left++;
            } else {
              const temp = this.triangleIndices[left];
              this.triangleIndices[left] = this.triangleIndices[right];
              this.triangleIndices[right] = temp;
              right--;
            }
          }
          return left;
        }

        computeOverallBounds() {
          let minX = Infinity, minY = Infinity, minZ = Infinity;
          let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
          for (let i = 0; i < this.triangleCount; i++) {
            const ti = i * 6;
            minX = Math.min(minX, this.triangleBounds[ti]);
            minY = Math.min(minY, this.triangleBounds[ti + 1]);
            minZ = Math.min(minZ, this.triangleBounds[ti + 2]);
            maxX = Math.max(maxX, this.triangleBounds[ti + 3]);
            maxY = Math.max(maxY, this.triangleBounds[ti + 4]);
            maxZ = Math.max(maxZ, this.triangleBounds[ti + 5]);
          }
          return { min: [minX, minY, minZ], max: [maxX, maxY, maxZ] };
        }

        getStats() {
          return {
            buildTimeMs: performance.now() - this.startTime,
            triangleCount: this.triangleCount,
            nodeCount: Math.max(1, 2 * Math.ceil(this.triangleCount / this.maxLeafTris) - 1)
          };
        }
      }

      self.onmessage = function(e) {
        const { type, meshId, positions, indices, options } = e.data;
        if (type === 'buildBVH') {
          try {
            const builder = new WorkerBVHBuilder(meshId, positions, indices, options || {});
            const bvhData = builder.build();
            const stats = builder.getStats();
            self.postMessage({
              type: 'complete', meshId, bvhData,
              buildTimeMs: stats.buildTimeMs,
              triangleCount: stats.triangleCount,
              nodeCount: stats.nodeCount,
              success: bvhData !== null
            });
          } catch (error) {
            self.postMessage({
              type: 'complete', meshId, bvhData: null,
              buildTimeMs: 0, triangleCount: 0, nodeCount: 0,
              success: false, error: error.message
            });
          }
        }
      };
    `;

    try {
      const blob = new Blob([workerCode], { type: 'application/javascript' });
      // 🚀 PERFORMANS OPT: Store blob URL for cleanup
      this.bvhWorkerBlobUrl = URL.createObjectURL(blob);
      this.bvhWorker = new Worker(this.bvhWorkerBlobUrl);

      this.bvhWorker.onerror = (e) => {
        console.error('BVH Worker error:', e);
        this.currentlyBuilding = 0;
        this.processNextInQueue();
      };

      this.bvhWorker.onmessage = (e) => {
        const { type, meshId, progress, success, buildTimeMs, triangleCount, stage } = e.data;

        if (type === 'progress') {
          const info = this.largeMeshes.get(meshId);
          if (info) {
            info.bvhProgress = progress;
          }
        }

        if (type === 'complete') {
          const info = this.largeMeshes.get(meshId);
          if (info) {
            info.bvhProgress = 100;
            info.bvhStatus = success ? 'ready' : 'failed';
            this.currentlyBuilding--;

            // Update stats
            if (success) {
              this.stats.bvhReady++;
              this.stats.bvhBuilding--;
              this.stats.totalBuildTime += buildTimeMs;
              this.stats.avgBuildTime = this.stats.totalBuildTime / this.stats.bvhReady;

              // If worker-built BVH succeeded, also build with three-mesh-bvh for actual raycasting
              // (Worker BVH is for progress tracking, three-mesh-bvh is for actual intersection)
              this.buildThreeMeshBVH(info);
            }

            this.processNextInQueue();
          }
        }

        const callback = this.workerPending.get(meshId);
        if (callback && type === 'complete') {
          callback(success);
          this.workerPending.delete(meshId);
        }
      };
    } catch (e) {
      this.bvhWorker = null;
    }
  }

  /**
   * Build three-mesh-bvh after worker completes
   * This is fast since worker already computed the structure
   */
  private buildThreeMeshBVH(info: LargeMeshInfo): void {
    try {
      // three-mesh-bvh needs to run on main thread but should be faster
      // since we've already "warmed up" the data in the worker
      info.mesh.geometry.computeBoundsTree({
        maxLeafTris: 10,
        strategy: SAH
      });
    } catch (e) {
      // If this fails, the worker-built structure is still usable for approximate raycasting
    }
  }

  /**
   * Register a mesh for large geometry handling
   * Call this when mesh is imported
   */
  registerMesh(mesh: THREE.Mesh): void {
    if (!mesh.geometry) return;

    const triangleCount = this.getTriangleCount(mesh.geometry);

    // Skip small meshes
    if (triangleCount < this.config.largeTriangleThreshold) {
      return;
    }

    const meshId = mesh.uuid;

    // Already registered?
    if (this.largeMeshes.has(meshId)) {
      return;
    }

    // Compute bounds (cheap operation)
    mesh.geometry.computeBoundingBox();
    mesh.geometry.computeBoundingSphere();

    const info: LargeMeshInfo = {
      mesh,
      triangleCount,
      bvhStatus: 'none',
      bvhProgress: 0,
      boundingBox: mesh.geometry.boundingBox!.clone(),
      boundingSphere: mesh.geometry.boundingSphere!.clone(),
      lastAccessTime: performance.now(),
      priority: this.calculatePriority(mesh)
    };

    this.largeMeshes.set(meshId, info);
    this.stats.largeMeshCount++;

    // Pre-warm BVH if enabled
    if (this.config.prewarmOnImport) {
      this.queueBVHBuild(info);
    }
  }

  /**
   * Register all meshes in a scene/group
   */
  registerScene(root: THREE.Object3D): void {
    root.traverse(child => {
      if (child instanceof THREE.Mesh) {
        this.registerMesh(child);
      }
    });
  }

  /**
   * Queue BVH build for a mesh
   */
  private queueBVHBuild(info: LargeMeshInfo): void {
    if (info.bvhStatus !== 'none') return;

    info.bvhStatus = 'building';
    info.buildStartTime = performance.now();
    this.stats.bvhBuilding++;

    // Sort by priority (higher priority first)
    this.buildQueue.push(info);
    this.buildQueue.sort((a, b) => b.priority - a.priority);

    this.processNextInQueue();
  }

  /**
   * Process next BVH build in queue
   */
  private processNextInQueue(): void {
    if (this.currentlyBuilding >= this.config.maxConcurrentBuilds) {
      return;
    }

    const next = this.buildQueue.shift();
    if (!next) return;

    this.currentlyBuilding++;
    this.buildBVHProgressive(next);
  }

  /**
   * Build BVH progressively (non-blocking)
   * FAZA 3.2: Use worker for off-main-thread construction
   */
  private async buildBVHProgressive(info: LargeMeshInfo): Promise<void> {
    const mesh = info.mesh;
    const geometry = mesh.geometry;

    if (!geometry) {
      info.bvhStatus = 'failed';
      this.currentlyBuilding--;
      this.processNextInQueue();
      return;
    }

    try {
      // 🚀 FAZA 3.2: Try worker-based build first (zero UI blocking)
      if (this.bvhWorker && info.triangleCount > this.config.largeTriangleThreshold) {
        const success = await this.buildBVHWithWorker(info);
        if (success) {
          return; // Worker handles completion
        }
        // Fall through to fallback if worker fails
      }

      // Fallback: For huge meshes, use progressive building with yields
      if (info.triangleCount > this.config.hugeTriangleThreshold) {
        await this.buildBVHWithYield(info);
      } else {
        // For large but not huge, build in chunks
        await this.buildBVHChunked(info);
      }
    } catch (e) {
      info.bvhStatus = 'failed';
    }

    this.currentlyBuilding--;
    this.processNextInQueue();
  }

  /**
   * Build BVH using Web Worker (FAZA 3.2)
   * Transfers geometry data to worker for off-main-thread processing
   */
  private async buildBVHWithWorker(info: LargeMeshInfo): Promise<boolean> {
    return new Promise((resolve) => {
      const geometry = info.mesh.geometry;
      const meshId = info.mesh.uuid;

      // Extract geometry data
      const positionAttr = geometry.getAttribute('position');
      if (!positionAttr) {
        resolve(false);
        return;
      }

      // Get positions and indices
      const positions = new Float32Array(positionAttr.array);
      let indices: Uint32Array | undefined;

      if (geometry.index) {
        indices = new Uint32Array(geometry.index.array);
      }

      // Store callback for completion
      this.workerPending.set(meshId, (success: boolean) => {
        resolve(success);
      });

      // Send to worker with transferable arrays for zero-copy
      try {
        const message = {
          type: 'buildBVH',
          meshId,
          positions,
          indices,
          options: {
            maxLeafTris: 10,
            strategy: SAH
          }
        };

        // Transfer arrays to worker (zero-copy)
        const transferables: Transferable[] = [positions.buffer];
        if (indices) {
          transferables.push(indices.buffer);
        }

        this.bvhWorker!.postMessage(message, transferables);
      } catch (e) {
        this.workerPending.delete(meshId);
        resolve(false);
      }

      // Timeout: if worker doesn't respond in 30 seconds, fall back
      setTimeout(() => {
        if (this.workerPending.has(meshId)) {
          this.workerPending.delete(meshId);
          resolve(false);
        }
      }, 30000);
    });
  }

  /**
   * Build BVH with periodic yields to prevent blocking
   */
  private async buildBVHWithYield(info: LargeMeshInfo): Promise<void> {
    const geometry = info.mesh.geometry;

    // Use requestIdleCallback for truly non-blocking builds
    return new Promise((resolve) => {
      const buildOptions: MeshBVHOptions = {
        maxLeafTris: 10,
        strategy: SAH  // SAH strategy for quality
      };

      // Schedule BVH build during idle time
      const idleCallback = (deadline: IdleDeadline) => {
        const startTime = performance.now();

        try {
          // Build BVH (this is still somewhat blocking but we limit it)
          geometry.computeBoundsTree(buildOptions);

          info.bvhStatus = 'ready';
          info.bvhProgress = 100;
          this.stats.bvhReady++;
          this.stats.bvhBuilding--;

          const buildTime = performance.now() - (info.buildStartTime || startTime);
          this.stats.totalBuildTime += buildTime;
          this.stats.avgBuildTime = this.stats.totalBuildTime / this.stats.bvhReady;
        } catch (e) {
          info.bvhStatus = 'failed';
        }

        resolve();
      };

      // Use requestIdleCallback if available, otherwise setTimeout
      if (typeof requestIdleCallback !== 'undefined') {
        requestIdleCallback(idleCallback, { timeout: 1000 });
      } else {
        setTimeout(() => idleCallback({ timeRemaining: () => 50, didTimeout: false }), 0);
      }
    });
  }

  /**
   * Build BVH in chunks
   */
  private async buildBVHChunked(info: LargeMeshInfo): Promise<void> {
    const geometry = info.mesh.geometry;

    return new Promise((resolve) => {
      // Simulate progress
      const progressInterval = setInterval(() => {
        info.bvhProgress = Math.min(info.bvhProgress + 20, 90);
      }, 50);

      // Schedule actual build
      setTimeout(() => {
        try {
          geometry.computeBoundsTree({
            maxLeafTris: 10,
            strategy: SAH
          });

          info.bvhStatus = 'ready';
          info.bvhProgress = 100;
          this.stats.bvhReady++;
          this.stats.bvhBuilding--;

          const buildTime = performance.now() - (info.buildStartTime || 0);
          this.stats.totalBuildTime += buildTime;
          this.stats.avgBuildTime = this.stats.totalBuildTime / this.stats.bvhReady;

        } catch (e) {
          info.bvhStatus = 'failed';
        }

        clearInterval(progressInterval);
        resolve();
      }, 0);
    });
  }

  /**
   * Perform raycast on large mesh
   * Returns approximate result if BVH not ready
   */
  raycast(
    mesh: THREE.Mesh,
    raycaster: THREE.Raycaster
  ): SelectionResult {
    const meshId = mesh.uuid;
    const info = this.largeMeshes.get(meshId);

    // Not a large mesh, use normal raycast
    if (!info) {
      const intersects = raycaster.intersectObject(mesh, false);
      if (intersects.length > 0) {
        this.stats.preciseSelections++;
        return {
          object: intersects[0].object,
          point: intersects[0].point,
          distance: intersects[0].distance,
          face: intersects[0].face,
          faceIndex: intersects[0].faceIndex ?? null,
          isApproximate: false,
          bvhProgress: 100
        };
      }
      return this.emptyResult();
    }

    // Update access time and priority
    info.lastAccessTime = performance.now();
    info.priority = this.calculatePriority(mesh);

    // BVH ready - use precise raycast
    if (info.bvhStatus === 'ready') {
      return this.preciseRaycast(mesh, raycaster, info);
    }

    // BVH building - use approximate selection
    if (info.bvhStatus === 'building' && this.config.approximateSelectionEnabled) {
      return this.approximateRaycast(mesh, raycaster, info);
    }

    // BVH not started - queue it and use approximate
    if (info.bvhStatus === 'none') {
      this.queueBVHBuild(info);
      if (this.config.approximateSelectionEnabled) {
        return this.approximateRaycast(mesh, raycaster, info);
      }
    }

    // Fallback: slow but accurate (blocking)
    return this.slowRaycast(mesh, raycaster, info);
  }

  /**
   * Precise raycast using BVH
   */
  private preciseRaycast(
    mesh: THREE.Mesh,
    raycaster: THREE.Raycaster,
    info: LargeMeshInfo
  ): SelectionResult {
    const intersects = raycaster.intersectObject(mesh, false);
    this.stats.preciseSelections++;

    if (intersects.length > 0) {
      return {
        object: intersects[0].object,
        point: intersects[0].point,
        distance: intersects[0].distance,
        face: intersects[0].face,
        faceIndex: intersects[0].faceIndex ?? null,
        isApproximate: false,
        bvhProgress: 100
      };
    }

    return this.emptyResult();
  }

  /**
   * Approximate raycast using bounding volumes
   * Fast but less accurate - good for initial feedback
   */
  private approximateRaycast(
    mesh: THREE.Mesh,
    raycaster: THREE.Raycaster,
    info: LargeMeshInfo
  ): SelectionResult {
    this.stats.approximateSelections++;

    // Transform bounding box to world space
    _tempBox3.copy(info.boundingBox);
    _tempBox3.applyMatrix4(mesh.matrixWorld);

    // Quick bounding box intersection test
    const ray = raycaster.ray;
    const intersectPoint = new THREE.Vector3();

    if (ray.intersectBox(_tempBox3, intersectPoint)) {
      // Hit bounding box - return approximate result
      const distance = ray.origin.distanceTo(intersectPoint);

      return {
        object: mesh,
        point: intersectPoint.clone(),
        distance,
        face: null,
        faceIndex: null,
        isApproximate: true,
        bvhProgress: info.bvhProgress
      };
    }

    return {
      ...this.emptyResult(),
      bvhProgress: info.bvhProgress
    };
  }

  /**
   * Slow raycast - blocking, use as last resort
   */
  private slowRaycast(
    mesh: THREE.Mesh,
    raycaster: THREE.Raycaster,
    info: LargeMeshInfo
  ): SelectionResult {
    const intersects = raycaster.intersectObject(mesh, false);

    if (intersects.length > 0) {
      return {
        object: intersects[0].object,
        point: intersects[0].point,
        distance: intersects[0].distance,
        face: intersects[0].face,
        faceIndex: intersects[0].faceIndex ?? null,
        isApproximate: false,
        bvhProgress: info.bvhProgress
      };
    }

    return {
      ...this.emptyResult(),
      bvhProgress: info.bvhProgress
    };
  }

  /**
   * Empty result
   */
  private emptyResult(): SelectionResult {
    return {
      object: null,
      point: null,
      distance: Infinity,
      face: null,
      faceIndex: null,
      isApproximate: false,
      bvhProgress: 100
    };
  }

  /**
   * Check if mesh is large
   */
  isLargeMesh(mesh: THREE.Mesh): boolean {
    return this.largeMeshes.has(mesh.uuid);
  }

  /**
   * Check if BVH is ready for mesh
   */
  isBVHReady(mesh: THREE.Mesh): boolean {
    const info = this.largeMeshes.get(mesh.uuid);
    return info?.bvhStatus === 'ready';
  }

  /**
   * Get BVH progress for mesh (0-100)
   */
  getBVHProgress(mesh: THREE.Mesh): number {
    const info = this.largeMeshes.get(mesh.uuid);
    return info?.bvhProgress ?? 100;
  }

  /**
   * Force BVH build now (blocking)
   */
  forceBVHBuild(mesh: THREE.Mesh): void {
    const info = this.largeMeshes.get(mesh.uuid);
    if (!info || info.bvhStatus === 'ready') return;

    try {
      mesh.geometry.computeBoundsTree({
        maxLeafTris: 10,
        strategy: SAH
      });
      info.bvhStatus = 'ready';
      info.bvhProgress = 100;
    } catch (e) {
      info.bvhStatus = 'failed';
    }
  }

  /**
   * Pre-warm all registered meshes
   */
  prewarmAll(): void {
    for (const info of this.largeMeshes.values()) {
      if (info.bvhStatus === 'none') {
        this.queueBVHBuild(info);
      }
    }
  }

  /**
   * Calculate priority based on visibility and distance
   */
  private calculatePriority(mesh: THREE.Mesh): number {
    let priority = 0;

    // Visible = higher priority
    if (mesh.visible) {
      priority += 100;
    }

    // Recently accessed = higher priority
    const info = this.largeMeshes.get(mesh.uuid);
    if (info) {
      const timeSinceAccess = performance.now() - info.lastAccessTime;
      if (timeSinceAccess < 1000) {
        priority += 50;
      }
    }

    return priority;
  }

  /**
   * Get triangle count from geometry
   */
  private getTriangleCount(geometry: THREE.BufferGeometry): number {
    if (geometry.index) {
      return geometry.index.count / 3;
    }
    const position = geometry.getAttribute('position');
    return position ? position.count / 3 : 0;
  }

  /**
   * Update - call each frame
   */
  update(): void {
    this.frameCount++;
    // Could add priority updates here
  }

  /**
   * Get statistics
   */
  getStats(): typeof this.stats & {
    queueLength: number;
    currentlyBuilding: number;
  } {
    return {
      ...this.stats,
      queueLength: this.buildQueue.length,
      currentlyBuilding: this.currentlyBuilding
    };
  }

  /**
   * Configure
   */
  configure(config: Partial<typeof this.config>): void {
    Object.assign(this.config, config);
  }

  /**
   * Unregister mesh
   */
  unregisterMesh(mesh: THREE.Mesh): void {
    const meshId = mesh.uuid;
    const info = this.largeMeshes.get(meshId);

    if (info) {
      // Dispose BVH
      if (info.bvhStatus === 'ready' && mesh.geometry.boundsTree) {
        mesh.geometry.disposeBoundsTree();
      }

      this.largeMeshes.delete(meshId);
      this.stats.largeMeshCount--;
    }
  }

  /**
   * Cleanup
   */
  dispose(): void {
    // Dispose all BVHs
    for (const info of this.largeMeshes.values()) {
      if (info.bvhStatus === 'ready' && info.mesh.geometry?.boundsTree) {
        info.mesh.geometry.disposeBoundsTree();
      }
    }

    this.largeMeshes.clear();
    this.buildQueue = [];

    if (this.bvhWorker) {
      this.bvhWorker.terminate();
      this.bvhWorker = null;
    }

    // 🚀 PERFORMANS OPT: Revoke blob URL to prevent memory leak
    if (this.bvhWorkerBlobUrl) {
      URL.revokeObjectURL(this.bvhWorkerBlobUrl);
      this.bvhWorkerBlobUrl = null;
    }
  }
}

/**
 * Smart Raycast Wrapper
 * Otomatik olarak büyük mesh'leri handle eder
 */
export class FikoSmartRaycast {
  private largeHandler: FikoLargeGeometryHandler;
  private raycaster: THREE.Raycaster;

  // Cache
  private lastResult: SelectionResult | null = null;
  private lastMouseX = -1;
  private lastMouseY = -1;

  constructor() {
    this.largeHandler = new FikoLargeGeometryHandler();
    this.raycaster = new THREE.Raycaster();
    this.raycaster.firstHitOnly = true;
  }

  /**
   * Register scene for large geometry handling
   */
  registerScene(scene: THREE.Scene): void {
    this.largeHandler.registerScene(scene);
  }

  /**
   * Perform smart raycast
   * Automatically uses appropriate method for each mesh
   */
  raycast(
    mouseX: number,
    mouseY: number,
    camera: THREE.Camera,
    scene: THREE.Scene
  ): SelectionResult {
    // Cache check
    if (mouseX === this.lastMouseX && mouseY === this.lastMouseY && this.lastResult) {
      return this.lastResult;
    }

    // Set up raycaster
    const _res = getCanvasResolution();
    const ndc = new THREE.Vector2(
      (mouseX / _res.width) * 2 - 1,
      -(mouseY / _res.height) * 2 + 1
    );
    this.raycaster.setFromCamera(ndc, camera);

    // Collect all meshes
    const meshes: THREE.Mesh[] = [];
    scene.traverse(child => {
      if (child instanceof THREE.Mesh && child.visible) {
        meshes.push(child);
      }
    });

    // Sort by distance to camera for early termination
    const cameraPos = camera.position;
    meshes.sort((a, b) => {
      a.getWorldPosition(_tempVec3);
      const distA = _tempVec3.distanceToSquared(cameraPos);
      b.getWorldPosition(_tempVec3);
      const distB = _tempVec3.distanceToSquared(cameraPos);
      return distA - distB;
    });

    // Raycast each mesh
    let bestResult: SelectionResult = {
      object: null,
      point: null,
      distance: Infinity,
      face: null,
      faceIndex: null,
      isApproximate: false,
      bvhProgress: 100
    };

    for (const mesh of meshes) {
      // Use large handler for large meshes
      if (this.largeHandler.isLargeMesh(mesh)) {
        const result = this.largeHandler.raycast(mesh, this.raycaster);
        if (result.distance < bestResult.distance) {
          bestResult = result;
        }
      } else {
        // Normal raycast for small meshes
        const intersects = this.raycaster.intersectObject(mesh, false);
        if (intersects.length > 0 && intersects[0].distance < bestResult.distance) {
          bestResult = {
            object: intersects[0].object,
            point: intersects[0].point,
            distance: intersects[0].distance,
            face: intersects[0].face,
            faceIndex: intersects[0].faceIndex ?? null,
            isApproximate: false,
            bvhProgress: 100
          };
        }
      }

      // Early termination for precise hits
      if (bestResult.object && !bestResult.isApproximate) {
        break;
      }
    }

    // Cache result
    this.lastMouseX = mouseX;
    this.lastMouseY = mouseY;
    this.lastResult = bestResult;

    return bestResult;
  }

  /**
   * Pre-warm BVH for visible meshes
   */
  prewarm(): void {
    this.largeHandler.prewarmAll();
  }

  /**
   * Get large geometry handler for direct access
   */
  getLargeHandler(): FikoLargeGeometryHandler {
    return this.largeHandler;
  }

  /**
   * Get stats
   */
  getStats(): ReturnType<FikoLargeGeometryHandler['getStats']> {
    return this.largeHandler.getStats();
  }

  /**
   * Cleanup
   */
  dispose(): void {
    this.largeHandler.dispose();
  }
}

// BVH types are already declared in three-mesh-bvh package
// No need to redeclare computeBoundsTree/disposeBoundsTree
